// src/components/exam-player/renderers/CTestRenderer.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

/** طول جای‌خالی را از روی یک جوابِ تکی به‌دست می‌آوریم */
function lengthFromBlank(b) {
  const ans = (b?.answer || "").trim();
  return Math.max(1, ans.length || 0);
}

/** ترتیب کلیدهای {{k}} را از متن استخراج می‌کند */
function extractKeyOrder(html = "") {
  const rx = /\{\{(\w+)\}\}/g;
  const out = [];
  let m;
  while ((m = rx.exec(html))) out.push(String(m[1]));
  return out;
}

/** در صورت نیاز Sanitization اضافه کنید */
function sanitizeHtml(html = "") {
  return html;
}

export default function CTestRenderer({
  item,
  onAnswerChange,
  initialAnswers,
  questionNumber,
  hideStem,
}) {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language?.includes("fa");

  const textHtml = item?.body?.text?.fa || item?.body?.text?.en || "";
  const blanksArr = Array.isArray(item?.body?.blanks) ? item.body.blanks : [];

  const blankByKey = useMemo(() => {
    const map = {};
    for (const b of blanksArr) {
      if (b?.key) map[String(b.key)] = b;
    }
    return map;
  }, [blanksArr]);

  const keyOrder = useMemo(() => extractKeyOrder(textHtml), [textHtml]);

  const [answersState, setAnswersState] = useState(() => {
    const base = {};
    keyOrder.forEach((k) => {
      const b = blankByKey[k] || {};
      const len = lengthFromBlank(b);
      const init =
        typeof initialAnswers?.[k] === "string"
          ? initialAnswers[k].slice(0, len).split("")
          : [];
      base[k] = Array.from({ length: len }, (_, i) => init[i] || "");
    });
    return base;
  }, [keyOrder, blankByKey, initialAnswers]);

  const slotRefs = useRef({});

  useEffect(() => {
    setAnswersState((prev) => {
      const next = {};
      keyOrder.forEach((k) => {
        const b = blankByKey[k] || {};
        const len = lengthFromBlank(b);
        const old = Array.isArray(prev[k]) ? prev[k] : [];
        next[k] = Array.from({ length: len }, (_, i) => old[i] || "");
      });
      return next;
    });
  }, [textHtml, blanksArr, isRtl, keyOrder, blankByKey]);

  useEffect(() => {
    const out = {};
    Object.keys(answersState).forEach((k) => {
      out[k] = (answersState[k] || []).join("");
    });
    onAnswerChange?.(out);
  }, [answersState, onAnswerChange]);

  const focusSlot = (key, idx) => {
    const el = slotRefs.current?.[key]?.[idx];
    if (el && el.focus) el.focus();
  };

  // فوکوس هوشمند: حرکت بین اسلات‌های یک blank و پرش به blank بعدی/قبلی
  const focusNextSlot = (currentKey, currentIdx) => {
    const currentLen = answersState[currentKey]?.length || 0;
    if (currentIdx + 1 < currentLen) {
      focusSlot(currentKey, currentIdx + 1);
    } else {
      const keyIndex = keyOrder.indexOf(currentKey);
      if (keyIndex !== -1 && keyIndex + 1 < keyOrder.length) {
        const nextKey = keyOrder[keyIndex + 1];
        focusSlot(nextKey, 0);
      }
    }
  };

  const focusPrevSlot = (currentKey, currentIdx) => {
    if (currentIdx > 0) {
      focusSlot(currentKey, currentIdx - 1);
    } else {
      const keyIndex = keyOrder.indexOf(currentKey);
      if (keyIndex > 0) {
        const prevKey = keyOrder[keyIndex - 1];
        const prevLen = answersState[prevKey]?.length || 1;
        focusSlot(prevKey, prevLen - 1);
      }
    }
  };

  const setChar = (key, idx, ch) =>
    setAnswersState((prev) => {
      const line = [...(prev[key] || [])];
      line[idx] = ch;
      return { ...prev, [key]: line };
    });

  const handleChange = (key, idx, e) => {
    let v = e.target.value.slice(-1); // گرفتن آخرین کاراکتر تایپ شده
    v = isRtl ? v : v.toLowerCase();

    setChar(key, idx, v);

    if (v) {
      focusNextSlot(key, idx);
    }
  };

  const handleKeyDown = (key, idx, e) => {
    const isNextKey = isRtl ? e.key === "ArrowLeft" : e.key === "ArrowRight";
    const isPrevKey = isRtl ? e.key === "ArrowRight" : e.key === "ArrowLeft";

    if (e.key === "Backspace") {
      if (!answersState[key]?.[idx]) {
        e.preventDefault();
        const keyIndex = keyOrder.indexOf(key);
        if (idx > 0) {
          setChar(key, idx - 1, "");
          focusSlot(key, idx - 1);
        } else if (keyIndex > 0) {
          const prevKey = keyOrder[keyIndex - 1];
          const prevLen = answersState[prevKey]?.length || 1;
          setChar(prevKey, prevLen - 1, "");
          focusSlot(prevKey, prevLen - 1);
        }
      } else {
        setChar(key, idx, "");
      }
    } else if (isPrevKey) {
      e.preventDefault();
      focusPrevSlot(key, idx);
    } else if (isNextKey) {
      e.preventDefault();
      focusNextSlot(key, idx);
    }
  };

  // پشتیبانی از Paste پیوسته روی چند Blank پشت سر هم
  const handlePaste = (key, idx, e) => {
    const txt = (e.clipboardData?.getData("text") || "").trim();
    if (!txt) return;
    e.preventDefault();

    const chars = [...txt];
    let charIdx = 0;
    let currentKeyIdx = keyOrder.indexOf(key);
    let startSlotIdx = idx;

    setAnswersState((prev) => {
      const nextState = { ...prev };

      while (charIdx < chars.length && currentKeyIdx < keyOrder.length) {
        const k = keyOrder[currentKeyIdx];
        const line = [...(nextState[k] || [])];

        while (charIdx < chars.length && startSlotIdx < line.length) {
          line[startSlotIdx] = (
            isRtl ? chars[charIdx] : chars[charIdx].toLowerCase()
          ).slice(0, 1);
          charIdx++;
          startSlotIdx++;
        }

        nextState[k] = line;
        currentKeyIdx++;
        startSlotIdx = 0; // برای کلیدهای بعدی از اسلات 0 شروع کن
      }

      return nextState;
    });
  };

  const renderedHtml = useMemo(() => {
    slotRefs.current = {}; // پاکسازی ریفرنس‌ها قبل از بازسازی DOM
    const parser = new DOMParser();
    const doc = parser.parseFromString(sanitizeHtml(textHtml), "text/html");
    const rx = /\{\{(\w+)\}\}/g;

    const traverse = (node, key) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const parts = [];
        let last = 0;
        let m;
        while ((m = rx.exec(node.textContent))) {
          const before = node.textContent.slice(last, m.index);
          if (before) parts.push(before);

          const bKey = String(m[1]);
          const b = blankByKey[bKey] || {};
          const len = lengthFromBlank(b);

          if (!slotRefs.current[bKey]) slotRefs.current[bKey] = [];

          parts.push(
            <span
              key={`${key}-group-${m.index}`}
              className="inline-flex items-end gap-1 mx-1 align-baseline"
            >
              {Array.from({ length: len }, (_, i) => (
                <input
                  key={`${key}-slot-${m.index}-${i}`}
                  ref={(el) => {
                    if (!slotRefs.current[bKey]) slotRefs.current[bKey] = [];
                    slotRefs.current[bKey][i] = el;
                  }}
                  value={answersState[bKey]?.[i] || ""}
                  onChange={(e) => handleChange(bKey, i, e)}
                  onKeyDown={(e) => handleKeyDown(bKey, i, e)}
                  onPaste={(e) => handlePaste(bKey, i, e)}
                  className="
                    w-6 text-center
                    border-b border-gray-400
                    focus:outline-none focus:border-indigo-500
                  "
                  placeholder="_"
                  aria-label={`ct-slot-${bKey}-${i + 1}`}
                  inputMode="text"
                  autoComplete="off"
                  spellCheck={false}
                />
              ))}
            </span>
          );

          last = rx.lastIndex;
        }
        const tail = node.textContent.slice(last);
        if (tail) parts.push(tail);
        return parts;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const tag = node.tagName.toLowerCase();
        const props = { key };
        for (const attr of node.attributes) {
          const name = attr.name;
          const value = attr.value;
          if (name === "class") props.className = value;
          else if (name === "style") {
            const style = {};
            value.split(";").forEach((decl) => {
              const [p, ...r] = decl.split(":");
              if (!p || !r.length) return;
              const camel = p
                .trim()
                .replace(/-([a-z])/g, (_, c) => c.toUpperCase());
              style[camel] = r.join(":").trim();
            });
            props.style = style;
          } else {
            props[name] = value;
          }
        }
        const children = [];
        let i = 0;
        for (let ch = node.firstChild; ch; ch = ch.nextSibling) {
          children.push(traverse(ch, `${key}-${i++}`));
        }
        return React.createElement(tag, props, ...children);
      }
      return null;
    };

    const children = [];
    let i = 0;
    for (let ch = doc.body.firstChild; ch; ch = ch.nextSibling) {
      children.push(traverse(ch, `root-${i++}`));
    }
    return <>{children}</>;
  }, [textHtml, blankByKey, answersState, isRtl]);

  if (!textHtml || keyOrder.length === 0) {
    return (
      <div className="text-sm text-red-500">
        {t("preview.cloze_no_text_error")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div
        className="
          prose prose-sm max-w-none leading-7
          [&_table]:table-fixed [&_table]:w-full
          [&_th]:px-3 [&_th]:py-2 [&_td]:px-3 [&_td]:py-2
          [&_thead_th]:text-left
        "
      >
        <div dir={isRtl ? "rtl" : "ltr"}>{renderedHtml}</div>
      </div>

      {!hideStem && (
        <div className="mt-2 text-xs text-gray-600">
          {t("preview.blanks_count", { n: keyOrder.length })}
        </div>
      )}
    </div>
  );
}